import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Play, Square, RotateCcw, DoorOpen, DoorClosed } from "lucide-react";
import { useVaultWarning } from "@/context/VaultWarningContext";
import { createGlowStyle } from "@/lib/glowUtils";

const OPEN_SEC = 60;
const CLOSED_SEC = 20 * 60;
const CYCLE_SEC = OPEN_SEC + CLOSED_SEC;

function pad(n) {
  return n.toString().padStart(2, "0");
}

function computePhase(startedAt, now) {
  if (!startedAt) return null;
  const elapsed = Math.max(0, Math.floor((now - new Date(startedAt).getTime()) / 1000));
  const inCycle = elapsed % CYCLE_SEC;
  if (inCycle < OPEN_SEC) {
    return { phase: "open", remaining: OPEN_SEC - inCycle };
  }
  return { phase: "closed", remaining: CYCLE_SEC - inCycle };
}

export default function VaultDoorTimer() {
  const [running, setRunning] = useState(false);
  const [startedAt, setStartedAt] = useState(null);
  const [now, setNow] = useState(() => Date.now());
  const [busy, setBusy] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const lastPhaseRef = useRef(null);
  const transitionTimerRef = useRef(null);

  // Tick every second while running
  useEffect(() => {
    if (!running) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [running]);

  // Load state on mount, poll for shared updates, and refresh on tab focus
  useEffect(() => {
    let mounted = true;
    const fetchState = async () => {
      try {
        const { data } = await api.get("/vault-timer");
        if (!mounted) return;
        setRunning(data.running);
        setStartedAt(data.started_at);
      } catch (e) {}
    };
    fetchState();
    const id = setInterval(fetchState, 4000);
    const onFocus = () => {
      if (document.visibilityState === "visible") fetchState();
    };
    document.addEventListener("visibilitychange", onFocus);
    window.addEventListener("focus", onFocus);
    return () => {
      mounted = false;
      clearInterval(id);
      document.removeEventListener("visibilitychange", onFocus);
      window.removeEventListener("focus", onFocus);
    };
  }, []);

  const phaseInfo = running ? computePhase(startedAt, now) : null;

  const { setWarningState, pulseIntensity } = useVaultWarning();

  // Sync warning state up to the root-level overlay via context
  useEffect(() => {
    const isWarning = !!(phaseInfo && phaseInfo.remaining <= 5);
    const isOpen = phaseInfo?.phase === "open";
    setWarningState({ isWarning, isOpen });
  }, [phaseInfo, setWarningState]);

  // Phase-flip toast + transition flash (feedback when cycle transitions)
  useEffect(() => {
    if (!phaseInfo) {
      lastPhaseRef.current = null;
      return;
    }
    if (lastPhaseRef.current && lastPhaseRef.current !== phaseInfo.phase) {
      // Trigger the flash animation; clear any previous timer first
      if (transitionTimerRef.current) clearTimeout(transitionTimerRef.current);
      setIsTransitioning(true);
      transitionTimerRef.current = setTimeout(() => setIsTransitioning(false), 800);
    }
    lastPhaseRef.current = phaseInfo.phase;
  }, [phaseInfo?.phase]); // eslint-disable-line react-hooks/exhaustive-deps

  // Clean up transition timer on unmount
  useEffect(() => () => {
    if (transitionTimerRef.current) clearTimeout(transitionTimerRef.current);
  }, []);


  const persist = async (next) => {
    setBusy(true);
    try {
      const { data } = await api.put("/vault-timer", {
        running: next.running,
        started_at: next.started_at ? new Date(next.started_at).toISOString() : null,
      });
      setRunning(data.running);
      setStartedAt(data.started_at);
    } catch (e) {
    } finally {
      setBusy(false);
    }
  };

  const handleStart = () => {
    if (running) return;
    persist({ running: true, started_at: new Date().toISOString() });
  };

  const handleStop = () => {
    persist({ running: false, started_at: null });
  };

  const handleReset = () => {
    // Reset to the start of whichever phase is currently active.
    // If open: a fresh started_at of "now" puts us at the very beginning of
    //   the open window (inCycle = 0 → open phase, 60 s remaining).
    // If closed: offset started_at back by OPEN_SEC so that inCycle = OPEN_SEC,
    //   which is the very first second of the closed phase (20 min remaining).
    const currentPhase = phaseInfo?.phase ?? "open";
    const offsetMs = currentPhase === "closed" ? OPEN_SEC * 1000 : 0;
    const newStartedAt = new Date(Date.now() - offsetMs).toISOString();
    persist({ running: true, started_at: newStartedAt });
  };

  // Visuals
  const isOpen = phaseInfo?.phase === "open";
  const isClosed = phaseInfo?.phase === "closed";

  // Warning: 5 seconds before a state change
  const isWarning = phaseInfo && phaseInfo.remaining <= 5;
  // About to close → red pulse; about to open → green pulse
  const warningClass = isWarning
    ? isOpen
      ? "animate-vault-warn-close"
      : "animate-vault-warn-open"
    : "";

  let stateText = "Idle";
  let statePill = "border-slate-700 text-slate-400 bg-slate-800/40";
  let timerColor = "text-slate-400";
  let containerBorder = "border-orange-500/20";
  let glow = "";
  let Icon = DoorClosed;

  if (isOpen) {
    stateText = "Door Open";
    statePill = "border-emerald-500/60 text-emerald-300 bg-emerald-500/10";
    timerColor = "text-emerald-300";
    containerBorder = "border-emerald-500/50";
    glow = "shadow-[0_0_24px_rgba(52,211,153,0.4)_inset]";
    Icon = DoorOpen;
  } else if (isClosed) {
    stateText = "Door Closed";
    statePill = "border-orange-600/70 text-orange-300 bg-orange-600/10";
    timerColor = "text-orange-300";
    containerBorder = "border-orange-600/50";
    glow = "shadow-[0_0_24px_rgba(217,119,6,0.4)_inset]";
    Icon = DoorClosed;
  }

  const minutes = phaseInfo ? Math.floor(phaseInfo.remaining / 60) : 0;
  const seconds = phaseInfo ? phaseInfo.remaining % 60 : 0;
  const cycleProgressPct = phaseInfo
    ? ((isOpen ? OPEN_SEC - phaseInfo.remaining : OPEN_SEC + (CLOSED_SEC - phaseInfo.remaining)) /
        CYCLE_SEC) * 100
    : 0;

  return (
    <section
      data-testid="vault-door-timer"
      className={`relative w-full border ${containerBorder} ${glow} ${warningClass} ${isTransitioning ? "animate-vault-state-flash" : ""} bg-[#121822]/80 px-5 py-4 corner-tl corner-tr corner-bl corner-br transition-colors duration-300 shadow-[0_0_20px_rgba(6,182,212,0.08)_inset]`}
      style={isWarning ? { "--vault-pulse-intensity": pulseIntensity } : undefined}
    >
      <div className="flex flex-col md:flex-row md:items-center gap-4">
        <div className="min-w-0">
          <div className="text-[11px] tracking-[0.3em] uppercase text-slate-500">Ruin Station</div>
          <div className="font-bold text-base text-white truncate">Vault Door</div>
        </div>

        <div className="flex-1 flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-3">
          <div className="flex items-center gap-3 flex-1 sm:flex-none">
            <div
              className={`border tracking-[0.3em] uppercase font-bold transition-all duration-300 ${
                isTransitioning
                  ? `px-3.5 py-1.5 text-[13px] ${statePill} ring-1 ring-orange-400/50 scale-105`
                  : `px-2.5 py-1 text-[11px] ${statePill}`
              }`}
              data-testid="vault-state"
            >
              {stateText}
            </div>
            {isWarning && (
              <div
                className={`px-2 py-1 border text-[11px] tracking-[0.2em] uppercase font-bold ${
                  isOpen
                    ? "border-orange-600/70 text-orange-300 bg-orange-600/10"
                    : "border-emerald-500/70 text-emerald-300 bg-emerald-500/10"
                }`}
                data-testid="vault-warning"
              >
                !
              </div>
            )}
            <div
              className={`font-dseg7 tabular-nums text-3xl sm:text-4xl text-slate-200`}
              data-testid="vault-remaining"
              style={phaseInfo ? { textShadow: createGlowStyle(timerColor) } : undefined}
            >
              {phaseInfo ? `${pad(minutes)}:${pad(seconds)}` : "--:--"}
            </div>
          </div>

          {/* Cycle progress bar */}
          <div className="flex-1 hidden md:flex items-center gap-2">
            <div 
              className="flex-1 h-2 bg-slate-800/60 border border-slate-700/40 relative"
            >
              <div
                className={`h-full transition-[width] duration-1000`}
                style={{ 
                  width: `${cycleProgressPct}%`,
                  background: isOpen ? 'linear-gradient(135deg, rgba(52,211,153,0.9), rgba(16,185,129,1))' : isClosed ? 'linear-gradient(135deg, rgba(217,119,6,0.9), rgba(234,88,12,1))' : 'rgb(71,85,105)',
                  boxShadow: isOpen ? '0 0 8px rgba(52,211,153,1.0)' : isClosed ? '0 0 8px rgba(217,119,6,1.0)' : 'none'
                }}
              />
              {/* Open-phase marker at 60/1260 ≈ 4.76% */}
              <div
                className="absolute top-0 bottom-0 w-px bg-orange-400/40"
                style={{ left: `${(OPEN_SEC / CYCLE_SEC) * 100}%` }}
              />
            </div>
          </div>

          <div className="flex items-center gap-1.5 shrink-0">
            {!running ? (
              <button
                onClick={handleStart}
                disabled={busy}
                data-testid="vault-start"
                className="flex items-center gap-1.5 px-4 py-2 border border-emerald-500 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500 hover:text-black transition-colors text-[11px] tracking-widest uppercase disabled:opacity-40"
              >
                <Play className="w-3.5 h-3.5" />
                Start
              </button>
            ) : (
              <>
                <button
                  onClick={handleReset}
                  disabled={busy}
                  data-testid="vault-reset"
                  className="flex items-center gap-1.5 px-3 py-2 border border-slate-700 text-slate-300 hover:border-orange-500 hover:text-orange-400 transition-colors text-[11px] tracking-widest uppercase disabled:opacity-40"
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                  Reset
                </button>
                <button
                  onClick={handleStop}
                  disabled={busy}
                  data-testid="vault-stop"
                  className="flex items-center gap-1.5 px-3 py-2 border border-orange-600/70 text-orange-300 hover:bg-orange-600 hover:text-black hover:border-orange-600 transition-colors text-[11px] tracking-widest uppercase disabled:opacity-40"
                >
                  <Square className="w-3.5 h-3.5" />
                  Stop
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
